import { useQuery } from '@apollo/client'
import { FunctionComponent } from 'react'
import {
  BrandServiceLevel,
  Campaign,
  CampaignCostBreakdown,
} from '../../../gql/types'
import { GET_CAMPAIGN_COST_BREAKDOWN } from '../../../gql/campaigns'
import { BriefTextRow } from '../BriefRow'
import { Divider } from '../../Divider'
import { mapCurrencyToSign } from '../../../utils/mappers'

interface Props {
  campaign: Campaign
}

export const EnterpriseBrief: FunctionComponent<Props> = ({ campaign }) => {
  const isEnterpriseOrWhiteGlove =
    campaign.brand?.serviceLevel === BrandServiceLevel.enterprise ||
    campaign.brand?.serviceLevel === BrandServiceLevel.whiteGlove

  const { data } = useQuery<{
    getCampaignCostBreakdown: CampaignCostBreakdown
  }>(GET_CAMPAIGN_COST_BREAKDOWN, {
    variables: { campaignId: campaign.id },
    skip: !isEnterpriseOrWhiteGlove,
  })

  if (!isEnterpriseOrWhiteGlove) return null

  const costBreakdown = data?.getCampaignCostBreakdown
  const currencySign = mapCurrencyToSign(campaign.currency)

  return (
    <>
      <div className='@container'>
        <div className='grid @xl:grid-cols-4 @xl:gap-6 grid-cols-1 gap-2'>
          <div>
            <h3 className='text-base font-semibold leading-6 text-gray-900'>
              Enterprise
            </h3>
          </div>
          <div className='overflow-hidden bg-white shadow rounded-lg col-span-3'>
            <div className='border-t border-gray-200 px-4'>
              <dl className='@md:divide-y @md:divide-gray-200'>
                {campaign.videoQuantity != null &&
                  campaign.videoQuantity > 0 && (
                    <BriefTextRow
                      title='Number of Videos'
                      value={`${campaign.videoQuantity}`}
                    />
                  )}
                {campaign.customCreatorEarningsCents != null && (
                  <BriefTextRow
                    title='Base Creator Payout'
                    value={`${currencySign}${campaign.customCreatorEarningsCents / 100} per collaboration`}
                  />
                )}
                {campaign.creatorEarningsCentsBoosted > 0 && (
                  <BriefTextRow
                    title='Boost'
                    value={`+${currencySign}${campaign.creatorEarningsCentsBoosted / 100} per collaboration`}
                  />
                )}
                {costBreakdown && (
                  <>
                    <BriefTextRow
                      title='Cost Breakdown'
                      value={
                        <>
                          {costBreakdown.tiers.map((tier) => (
                            <span
                              key={tier.creatorEarningsCents}
                              className='block'
                            >
                              {tier.count} creator{tier.count !== 1 ? 's' : ''}{' '}
                              at {currencySign}
                              {tier.creatorEarningsCents / 100}
                            </span>
                          ))}
                          {costBreakdown.remainingSlots > 0 && (
                            <span className='block text-gray-500'>
                              {costBreakdown.remainingSlots} remaining slot
                              {costBreakdown.remainingSlots !== 1
                                ? 's'
                                : ''} at {currencySign}
                              {costBreakdown.remainingSlotRateCents / 100}
                            </span>
                          )}
                        </>
                      }
                    />
                    <BriefTextRow
                      title='Total Campaign Cost'
                      value={`${currencySign}${costBreakdown.totalCostCents / 100}`}
                    />
                  </>
                )}
                {!costBreakdown &&
                  campaign.customCreatorEarningsCents != null &&
                  campaign.videoQuantity > 0 && (
                    <BriefTextRow
                      title='Total Campaign Payout'
                      value={`${currencySign}${(campaign.customCreatorEarningsCents / 100) * campaign.videoQuantity}`}
                    />
                  )}
              </dl>
            </div>
          </div>
        </div>
      </div>
      <Divider className='py-8' />
    </>
  )
}
